import type { CSSProperties, KeyboardEvent } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export interface CommentComposerProps {
  onSubmit: (text: string) => void;
  onClose: () => void;
  className?: string;
  style?: CSSProperties;
}

export function CommentComposer({ onSubmit, onClose, className, style }: CommentComposerProps) {
  const [text, setText] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const trimmedText = useMemo(() => text.trim(), [text]);

  useEffect(() => {
    requestAnimationFrame(() => textareaRef.current?.focus());
  }, []);

  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as globalThis.Node)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', onClickOutside);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
    };
  }, [onClose]);

  const handleSubmit = useCallback(() => {
    if (!trimmedText) {
      return;
    }

    onSubmit(trimmedText);
  }, [onSubmit, trimmedText]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }

      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSubmit();
      }
    },
    [handleSubmit, onClose],
  );

  return (
    <div ref={containerRef} className={className} style={style}>
      <textarea
        ref={textareaRef}
        className="comment-input-textarea"
        placeholder="Add a comment..."
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={handleKeyDown}
        rows={2}
      />
      <div className="comment-input-actions">
        <button type="button" className="comment-input-cancel" onClick={onClose}>
          Cancel
        </button>
        <button type="button" className="comment-input-submit" onClick={handleSubmit} disabled={!trimmedText}>
          Comment
        </button>
      </div>
    </div>
  );
}
